// src/components/StickerPickerModal.jsx
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { supabase } from '../config/supabase';

export default function StickerPickerModal({
  isOpen,
  onClose,
  onSelected,
  schoolId,
  studentId,
  studentMasterId,
  lessonNoteId,
  noteBlockId,
  earnedFor,
}) {
  const [loading, setLoading] = useState(false);
  const [stickers, setStickers] = useState([]);
  const [error, setError] = useState(null);
  const [selectingId, setSelectingId] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState('All');
  const tabsContainerRef = useRef(null);
  const modalRef = useRef(null);

  // ───────────────────────────────── helpers
  const emojiForCategory = useCallback((c) => {
    const k = (c || 'Other').toLowerCase();
    if (k.includes('animal')) return '🐾';
    if (k.includes('music') || k.includes('note')) return '🎵';
    if (k.includes('star')) return '⭐️';
    if (k.includes('space') || k.includes('galaxy') || k.includes('planet')) return '🚀';
    if (k.includes('sport')) return '🏆';
    if (k.includes('food') || k.includes('snack') || k.includes('fruit')) return '🍓';
    if (k.includes('robot') || k.includes('tech')) return '🤖';
    if (k.includes('nature') || k.includes('leaf') || k.includes('plant')) return '🌿';
    if (k.includes('fantasy') || k.includes('fairy')) return '🧚';
    if (k.includes('face') || k.includes('emoji') || k.includes('smile')) return '😀';
    if (k.includes('letter') || k.includes('alphabet')) return '🔤';
    if (k.includes('number') || k.includes('math')) return '🔢';
    return '🎁';
  }, []);

  const popAndWiggle = async (el) => {
    try {
      if (!el?.animate) return;
      el.style.willChange = 'transform';
      const a = el.animate(
        [
          { transform: 'scale(1) rotate(0deg)' },
          { transform: 'scale(1.18) rotate(6deg)', offset: 0.35 },
          { transform: 'scale(1.12) rotate(-6deg)', offset: 0.6 },
          { transform: 'scale(1) rotate(0deg)' },
        ],
        { duration: 520, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'none' }
      );
      await a.finished;
    } catch { /* no-op */ }
  };

  // ───────────────────────────────── lifecycle
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => {
      try {
        modalRef.current?.querySelector('button, [href], input, select, textarea')?.focus();
      } catch {}
    }, 0);
  }, [isOpen]);

  useEffect(() => {
    const load = async () => {
      setLoading(true); setError(null);
      try {
        const { data, error } = await supabase
          .from('sticker_library')
          .select('id, category, label, image_url')
          .eq('is_active', true)
          .order('category', { ascending: true })
          .order('label', { ascending: true });
        if (error) throw error;
        setStickers(data || []);
      } catch {
        setError('Failed to load stickers.');
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) load();
  }, [isOpen]);

  const { grouped, categories } = useMemo(() => {
    const m = new Map();
    for (const s of stickers) {
      const k = s.category || 'Other';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(s);
    }
    const groups = Array.from(m.entries()).map(([k, arr]) => ({ category: k, items: arr }));
    return { grouped: groups, categories: ['All', ...groups.map(g => g.category)] };
  }, [stickers]);

  useEffect(() => {
    if (categories.length && !categories.includes(selectedCategory)) setSelectedCategory('All');
  }, [categories, selectedCategory]);

  const visibleItems = useMemo(() => {
    if (selectedCategory === 'All') return stickers;
    const g = grouped.find(g => g.category === selectedCategory);
    return g ? g.items : [];
  }, [grouped, stickers, selectedCategory]);

  const scrollTabIntoView = useCallback((idx) => {
    const root = tabsContainerRef.current;
    const el = root?.querySelector(`[data-tab-idx="${idx}"]`);
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, []);

  // ───────────────────────────────── select (insert → animate tile → close)
  const handleSelect = async (s, clickEvt) => {
    if (selectingId) return;
    setSelectingId(s.id);
    setError(null);

    try {
      if (!noteBlockId) throw new Error('Missing note_block_id.');

      const { error } = await supabase
        .from('student_stickers')
        .insert({
          school_id: schoolId,
          student_id: studentId,
          student_master_id: studentMasterId || null,
          lesson_note_id: lessonNoteId,
          note_block_id: noteBlockId,
          sticker_library_id: s.id,
          sticker_label_snapshot: s.label,
          sticker_image_url_snapshot: s.image_url,
          earned_for: earnedFor || 'Completed practice goal',
        })
        .select('id')
        .single();
      if (error) throw error;

      // notify tools to render immediately
      try {
        window.dispatchEvent(new CustomEvent('mms:sticker-added', {
          detail: { noteBlockId, imageUrl: s.image_url, label: s.label },
        }));
      } catch {}

      // BIGGER animation on the tile’s image
      const tile = clickEvt?.currentTarget;
      const img = tile?.querySelector('img') || tile;
      await popAndWiggle(img);

      onSelected?.(s);
      setTimeout(() => onClose?.(), 30);
    } catch {
      setError('Sorry—could not add your sticker.');
    } finally {
      setSelectingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000]">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1.5px]" onClick={onClose} />

      <div
        ref={modalRef}
        className="absolute left-1/2 top-1/2 w-[min(980px,94vw)] max-h-[86vh] -translate-x-1/2 -translate-y-1/2
                   rounded-2xl shadow-2xl border border-orange-200 bg-gradient-to-br from-[#fffcec] to-[#ffe8d1] p-4 sm:p-6"
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="text-2xl">🎉</div>
          <h2 className="text-xl sm:text-2xl font-extrabold text-[#201919]">You earned a sticker!</h2>
          <button onClick={onClose} className="ml-auto px-3 py-1.5 rounded-lg border border-black/10 bg-white hover:bg-black/5">Close</button>
        </div>

        {error && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">{error}</div>}

        {/* Category pills */}
        <div className="mb-4 -mx-1">
          <div ref={tabsContainerRef} className="flex items-center gap-2 px-1 overflow-x-auto no-scrollbar snap-x snap-mandatory" style={{ WebkitOverflowScrolling: 'touch' }}>
            {categories.map((c, idx) => {
              const active = c === selectedCategory;
              const emj = emojiForCategory(c);
              return (
                <button
                  key={c}
                  data-tab-idx={idx}
                  onClick={() => { setSelectedCategory(c); scrollTabIntoView(idx); }}
                  title={c === 'All' ? 'Show all stickers' : `Show ${c}`}
                  className={[
                    'snap-start shrink-0 px-3 sm:px-4 py-2 rounded-full border transition-all text-sm font-bold',
                    active ? 'bg-[#f97316] border-[#f97316] text-white shadow-md scale-[1.02]' : 'bg-white border-orange-200 text-[#201919] hover:bg-orange-50',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#f97316]',
                    'fun-pill'
                  ].join(' ')}
                >
                  <span className="mr-1">{emj}</span>{c}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sticker grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-32 bg-white/70 border border-orange-200/60 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="pr-1 overflow-y-auto max-h-[58vh]">
            {visibleItems.length === 0 ? (
              <div className="text-sm text-[#201919]/70 px-1 py-4">No stickers in <strong>{selectedCategory}</strong> yet.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {visibleItems.map((s) => (
                  <button
                    key={s.id}
                    onClick={(e) => handleSelect(s, e)}
                    disabled={!!selectingId}
                    title={s.label}
                    className="group relative bg-white rounded-2xl border border-orange-200/70 shadow-sm p-3 hover:shadow-md focus-ring transition transform hover:-translate-y-0.5"
                  >
                    <div className="w-full aspect-square flex items-center justify-center overflow-hidden">
                      <img src={s.image_url} alt={s.label} className="max-w-full max-h-full object-contain" />
                    </div>
                    <div className="mt-2 text-xs text-[#201919]/80 truncate">{s.label}</div>
                    {selectingId === s.id && (
                      <div className="absolute inset-0 bg-white/60 rounded-2xl grid place-items-center text-lg font-bold">Adding…</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <style>{`
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          .fun-pill:hover { animation: pill-wiggle 420ms ease-in-out; }
          @keyframes pill-wiggle {
            0% { transform: rotate(0deg) scale(1.02); }
            25% { transform: rotate(2deg) scale(1.03); }
            50% { transform: rotate(-2deg) scale(1.03); }
            100% { transform: rotate(0deg) scale(1.02); }
          }
        `}</style>
      </div>
    </div>
  );
}